import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox'
import { CheckboxGroup as CheckboxGroupPrimitive } from '@base-ui/react/checkbox-group'
import { VariantProps } from 'class-variance-authority'
import { CheckIcon, MinusIcon } from 'lucide-react'

import { cn, cva } from '@eigtech/ui/lib/utils'

// Define the variants for the Checkbox using cva.
const checkboxVariants = cva(
  'border-input shadow-xs group-has-disabled/field:opacity-50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 aria-invalid:aria-checked:border-primary dark:bg-input/30 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground dark:data-checked:bg-primary peer relative flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-[4px] border outline-none transition-shadow after:absolute after:-inset-x-3 after:-inset-y-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        sm: 'size-4',
        md: 'size-4.5',
        lg: 'size-5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const checkboxIndicatorVariants = cva('grid place-content-center text-current transition-none', {
  variants: {
    size: {
      sm: '[&>svg]:size-3',
      md: '[&>svg]:size-3.5',
      lg: '[&>svg]:size-4',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

function Checkbox({
  className,
  size = 'md',
  ...props
}: Omit<CheckboxPrimitive.Root.Props, 'children'> & VariantProps<typeof checkboxVariants>) {
  return (
    <CheckboxPrimitive.Root
      className={checkboxVariants({ size, className })}
      data-slot="checkbox"
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={checkboxIndicatorVariants({ size })}
        data-slot="checkbox-indicator"
        render={(props, state) =>
          state.indeterminate ? <MinusIcon {...props} /> : <CheckIcon {...props} />
        }
      />
    </CheckboxPrimitive.Root>
  )
}

function CheckboxGroup({ className, ...props }: CheckboxGroupPrimitive.Props) {
  return <CheckboxGroupPrimitive className={cn('', className)} {...props} />
}

// eslint-disable-next-line react-refresh/only-export-components
export { Checkbox, CheckboxGroup, checkboxVariants }
